import {
  signInWithPopup,
  GoogleAuthProvider,
  onIdTokenChanged,
} from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { auth } from '../config/firebase';
import { paths } from '../constants/paths';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { RootState } from '../redux/store';
import {
  fetchRockyUserData,
  fetchUserAccountsCounts,
  fetchUserData,
  fetchUsersList,
} from '../redux/userData';
import { getAccountAndProjectList } from '../redux/accounts';
import { AuthService, CUSTOM_LOGIN_EMAIL } from '../services/auth-service';
import { CookieService } from '../services/cookies';
import { SessionStorageService } from '../services/session-storage-service';
import { UserWithPermissions } from '../types';
import { isFirefox, parseLocation } from '../utils';

const NUMBERS_OF_RELOAD_KEY = 'numbersOfReload';
const LOGIN_POPUP_KEY = 'loginPopupKey';

export const getCustomEmail = () => {
  let { customEmail } = parseLocation<{ customEmail?: string }>();
  const customEmailFromCookie = CookieService.getCookie(CUSTOM_LOGIN_EMAIL);
  customEmail = customEmail ?? customEmailFromCookie;
  return customEmail;
};

const getReloadCount = () =>
  Number(SessionStorageService.getState<string>(NUMBERS_OF_RELOAD_KEY) ?? 0);

const increaseReloadCount = () => {
  if (!isFirefox) {
    return;
  }
  const next = getReloadCount() + 1;
  SessionStorageService.setState(NUMBERS_OF_RELOAD_KEY, next.toString());
};

export const useAuth = (): {
  user: UserWithPermissions | null;
  showInstructions: boolean;
} => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(authUserSelector);
  const [showInstructions, setShowInstructions] = useState(false);
  const customEmail = getCustomEmail();

  // Initialize to false. We will only set this to true when Firebase
  // explicitly hands us a valid, non-expired token.
  const [loginFlow, setLoginFlow] = useState<
    | 'custom-login-start'
    | 'custom-login-initiated'
    | 'custom-login-end'
    | 'default-flow-start'
    | 'default-flow-end'
  >(customEmail ? 'custom-login-start' : 'default-flow-start');

  const isRockyBonusesPage = window.location.pathname.includes(
    paths.rockyBonuses,
  );

  const fetchData = useCallback(() => {
    dispatch(fetchUserData());
    dispatch(fetchUserAccountsCounts());
    dispatch(fetchUsersList());
    dispatch(fetchRockyUserData());
    dispatch(getAccountAndProjectList({ showError: false }));
  }, [dispatch]);

  const signIn = useCallback(() => {
    if (!SessionStorageService.getState(LOGIN_POPUP_KEY)) {
      signInWithPopup(auth, new GoogleAuthProvider()).then(() => {
        location.reload();
      });
      SessionStorageService.setState(LOGIN_POPUP_KEY, true);
    }

    increaseReloadCount();
  }, []);

  useEffect(() => {
    if (loginFlow !== 'default-flow-end' && loginFlow !== 'custom-login-end') {
      return;
    }

    fetchData();
  }, [loginFlow, fetchData]);

  // Custom login flow:
  // 1. user is null, hasToken is false. If customEmail is set, we will login with customEmail and set hasToken to true
  // 2. user is still null, fetchData is triggered, hasToken is true so we don't rerun it
  // - if isRockyBonusesPage is true, we don't run it, since it's an iframe inside Rocky
  useEffect(() => {
    if (isRockyBonusesPage || loginFlow !== 'custom-login-start') {
      return;
    }

    const loginWithCustomEmail = async () => {
      const success = await AuthService.customLogin(customEmail);
      if (!success) {
        setLoginFlow('default-flow-start');
        return;
      }
      setLoginFlow((prevFlow) => {
        // This is to prevent cases from flow being reset to 'custom-login-initiated' second time
        // Since on local React calls all hooks twice
        if (prevFlow === 'custom-login-start') {
          return 'custom-login-initiated';
        }
        return prevFlow;
      });
    };

    void loginWithCustomEmail();
  }, [customEmail, isRockyBonusesPage, loginFlow]);

  useEffect(() => {
    // If it's a rocky bonuses page or not in default flow start, return
    if (isRockyBonusesPage) {
      return;
    }

    // onIdTokenChanged handles initial mount, sign-ins, and token refreshes automatically
    // That is, it is triggered on all of the above events
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      // This is to prevent cases when you try to use customEmail, but Firebase inititates before new custom login finishes
      // and 'onIdTokenChanged' is triggered with old user
      if (customEmail && firebaseUser?.email !== customEmail) {
        return;
      }

      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken();
          AuthService.setToken(token);

          // Transition to completion state based on current flow.
          // We handle 'custom-login-start' here to fix a race condition:
          // onIdTokenChanged can fire (via signInWithCustomToken) before the
          // setLoginFlow('custom-login-initiated') in loginWithCustomEmail runs.
          // In that case the email guard above already confirmed the user is correct.
          setLoginFlow((prevFlow) => {
            if (prevFlow === 'default-flow-start') {
              return 'default-flow-end';
            } else if (
              prevFlow === 'custom-login-initiated' ||
              prevFlow === 'custom-login-start'
            ) {
              return 'custom-login-end';
            }
            return prevFlow;
          });
        } catch {
          // Token fetch failed — user will remain unauthenticated
        }
      } else {
        // If FB is not initialized yet, but it's a custom login flow, then we don't need to sign in
        if (customEmail) return;

        const reloadCount = getReloadCount();

        if (isFirefox && reloadCount > 2) {
          setShowInstructions(true);
          return;
        }

        if (!isFirefox || reloadCount <= 2) {
          signIn();
        }
      }
    });

    // Clean up the subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [isRockyBonusesPage, signIn, customEmail]);

  return {
    user,
    showInstructions,
  };
};

const authUserSelector = (state: RootState): UserWithPermissions | null =>
  state.userData.user;
